import { formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import {
    IAttributeConfData,
    IAttributeConfDataOption
} from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalService } from '@df/ui/modal/modal.service';
import { TradeStoreService } from '../../../../../multisite/trade/trade-store.service';
import { LocalProduct } from '../../../../model/product/product.decorator';
import { ConfigurableOptionWarningComponent } from './configurable-option-warning.component';
import { NUi } from '@df/ui/df-ui.config';
export interface IConfigurableOptionWarning {
    id: TConfigurableOptionWarning;
    title?: string;
    subtitle: string;
    date?: string;
}
export enum TConfigurableOptionWarning {
    discontinued = 'discontinued',
    backorder = 'backorder',
    unavailable = 'unavailable'
}
@Injectable({
    providedIn: 'root'
})
export class ConfigurableOptionWarningService {
    private lastModalRef?: ModalRef<ConfigurableOptionWarningComponent>;
    warning?: {
        attributeCode: string;
        optionValue: number;
    };

    private uiConfig = inject(NUi.OPTIONS);

    constructor(private modalService: ModalService, private app: App, client: Client, private tradeStoreService: TradeStoreService) {
        client.obs.resize.subscribe(() => {
            if (this.lastModalRef) {
                this.cleanup();
            }
        });
    }
    setWarning(attribute: IAttributeConfData, option: IAttributeConfDataOption, element: HTMLElement): void {
        const optionWarning = this.getProductWarning(option);
        const warning = optionWarning
            ? {
                  attributeCode: attribute.code,
                  optionValue: option.value
              }
            : undefined;
        if (!warning) {
            this.cleanup();
        } else {
            this.cleanup();
            this.warning = warning;
            const rect = element.getBoundingClientRect();
            const width = 320;
            const height = 60;
            const top = rect.top - height - 20;
            const center = rect.left + 0.5 * rect.width;
            this.lastModalRef = this.modalService.open(ConfigurableOptionWarningComponent, {
                hasBackdrop: false,
                position: {
                    top: `${top < 200 ? rect.bottom + 20 : top}px`,
                    left: `${Math.max(10, Math.min(center - 0.5 * width, window.innerWidth - width - 10))}px`
                },
                data: optionWarning,
                scrollStrategy: 'close',
                width: `${width}px`,
                maxWidth: '95vw',
                minHeight: `${height}px`
            });
        }
    }
    unsetWarning(attribute: IAttributeConfData, option: IAttributeConfDataOption): void {
        const warning = this.warning;
        if (warning?.attributeCode === attribute.code && warning.optionValue === option.value) {
            this.cleanup();
        }
    }
    private cleanup() {
        this.lastModalRef?.close();
        this.lastModalRef = undefined;
        this.warning = undefined;
    }
    getProductWarning(option: IAttributeConfDataOption): Omit<IConfigurableOptionWarning, 'element'> | undefined {
        const product = option.product as LocalProduct<IProductDataV3>;
        if (product && product.data) {
            const inStockDate = product.data.in_stock_date ? new Date(product.data.in_stock_date) : new Date();
            const currentDate = new Date();
            if (
                ((product.isTempOutOfStock && inStockDate) || product.isBackSoon || product.backOrderOnly || product.isComingSoon) &&
                inStockDate > currentDate
            ) {
                return {
                    id: TConfigurableOptionWarning.backorder,
                    title: undefined,
                    subtitle: 'Estimated in stock',
                    date: `${formatDate(inStockDate, this.uiConfig.date.format, this.app.data.locale)}`
                };
            }

            if (!option.salable && product.isSoldOut) {
                return {
                    id: TConfigurableOptionWarning.discontinued,
                    title: 'Sold Out',
                    subtitle: 'This product is now discontinued'
                };
            }
            if (product.isTempOutOfStock && !inStockDate) {
                return {
                    id: TConfigurableOptionWarning.unavailable,
                    title: 'Currently Unavailable',
                    subtitle: 'Select your size and we’ll email you once it’s back in stock'
                };
            }
        }
        return undefined;
    }
}
